import { AR_INTL_LOCALE, isArabicUi } from "@/shared/config/arabicUi";
import { lecturesGateOn } from "./planStore";

/**
 * Все тексты пакетов — со спецификации и досок канваса (09.10.2026), на языках
 * сайта: ru, en, de, es, cn (китайский), ar (арабский). Живут здесь, а не в общих
 * locales/*.json, которые правят многие ветки. Без пределов, ставок и
 * округлений (тексты для людей — минимальные), цена — только на кнопке.
 */

export type PlanLang = "ru" | "en" | "de" | "es" | "cn" | "ar";

const LANGS: PlanLang[] = ["ru", "en", "de", "es", "cn", "ar"];

export const planLang = (lang: string | undefined | null): PlanLang => {
    const l = (lang || "").toLowerCase();
    if (l === "zh") return "cn";
    // Арабский — только когда включён арабский интерфейс (рубильник или предпросмотр,
    // shared/config/arabicUi); иначе /ar, как весь сайт там, — английский (D15).
    if (l === "ar") return isArabicUi(l) ? "ar" : "en";
    return (LANGS as string[]).includes(l) ? (l as PlanLang) : "en";
};

/** Русские формы: 1 вопрос, 2 вопроса, 5 вопросов. */
export const ruPlural = (n: number, forms: [string, string, string]): string => {
    const a = Math.abs(n) % 100;
    if (a >= 11 && a <= 19) return forms[2];
    const b = a % 10;
    if (b === 1) return forms[0];
    if (b >= 2 && b <= 4) return forms[1];
    return forms[2];
};

const LOCALE: Record<PlanLang, string> = { ru: "ru-RU", en: "en-GB", de: "de-DE", es: "es-ES", cn: "zh-CN", ar: AR_INTL_LOCALE };

const pad = (n: number) => String(n).padStart(2, "0");

const asDate = (v: string | Date | null | undefined): Date | null => {
    if (!v) return null;
    const d = v instanceof Date ? v : new Date(v);
    return Number.isFinite(d.getTime()) ? d : null;
};

/** 29.08.2027 (ru, de, es, ar); 29 Aug 2027 (en); 2027年8月29日 (cn). В часовом поясе человека. */
export const fmtDate = (v: string | Date | null | undefined, lang: string): string => {
    const d = asDate(v);
    if (!d) return "";
    const l = planLang(lang);
    if (l === "en" || l === "cn") {
        return new Intl.DateTimeFormat(LOCALE[l], { day: "numeric", month: "short", year: "numeric" }).format(d);
    }
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};

/** 12.01 (ru, de, es); 12 Jan (en); 1月12日 (cn). */
export const fmtDay = (v: string | Date | null | undefined, lang: string): string => {
    const d = asDate(v);
    if (!d) return "";
    const l = planLang(lang);
    if (l === "en" || l === "cn") {
        return new Intl.DateTimeFormat(LOCALE[l], { day: "numeric", month: "short" }).format(d);
    }
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}`;
};

/** $9.90 */
export const fmtPrice = (cents: number): string => `$${(Math.round(cents) / 100).toFixed(2)}`;

/** Кусок текста; `b` — выделить (число вопросов, дата). */
export interface Seg {
    t: string;
    b?: boolean;
}

export interface PlanCopy {
    packsTitle: string;
    packsSub: string;
    subLine: string;
    term: (months: number) => string;
    givesSearch: (term: string) => string;
    /** Слова после числа: «вопроса специалисту». */
    questionsTail: (n: number) => string;
    choose: string;
    chooseAria: (term: string, price: string, regular?: string) => string;
    foot: string;
    quiet: string;
    close: string;
    checkoutFailed: string;
    chatLabel: string;
    until: (date: string) => string;
    ended: (date: string) => string;
    renew: string;
    renewAria: string;
    remind7: (left: number, day: string) => string;
    remind1: (left: number) => string;
    chipAria: (date: string) => string;
    searchEnded: (date: string) => string;
    newAfterRenew: string;
    migration: (tokens: number, questions: number, date: string) => Seg[];
    gotIt: string;
    trialTitle: string;
    trialSub: (date: string) => string;
    appYearTitle: string;
    appYearSub: (date: string) => string;
    /** Месяц поиска в подарок за покупку (уведомление buyer_month). */
    buyerMonthTitle: string;
    buyerMonthSub: (date: string) => string;
    dailyCap: string;
    paidToast: (term: string | null, date: string) => string;
    /** «Всё равно настаиваю» в режиме пакетов: вопрос из пакета не расходуется. */
    insistNote: string;
    /** Кошелёк не прочитался у человека на пакетах (или режим неизвестен). */
    loadFailed: string;
    retry: string;
    /** Гостю при «all»: что даст вход — 7 дней поиска в подарок (не «бесплатные токены»). */
    guestTrial: string;
    guestLogin: string;
    guestTrialNote: string;
    /** Ссылка «поделиться» у человека на пакетах: только получить. */
    redeemTitle: string;
    redeemDone: string;
    /** Карточка «Все продукты». */
    productGives: string;
}

const RU: PlanCopy = {
    packsTitle: "Поиск и вопросы специалисту",
    packsSub: "Умный поиск на весь срок и вопросы специалисту",
    subLine: "Скидка для годовой подписки приложения",
    term: (m) => `${m} ${ruPlural(m, ["месяц", "месяца", "месяцев"])}`,
    givesSearch: (term) => `Умный поиск — ${term}`,
    questionsTail: (n) => `${ruPlural(n, ["вопрос", "вопроса", "вопросов"])} специалисту`,
    choose: "Выбрать",
    chooseAria: (term, price, regular) =>
        regular ? `Выбрать ${term}: ${price} вместо ${regular}` : `Выбрать ${term}: ${price}`,
    foot: "Продлите до конца срока — оставшиеся вопросы сохранятся.",
    quiet: "С годовой подпиской приложения — дешевле",
    close: "Закрыть",
    checkoutFailed: "Не удалось открыть оплату. Попробуйте ещё раз.",
    chatLabel: "Вопросов специалисту:",
    until: (d) => `до ${d}`,
    ended: (d) => `срок закончился ${d}`,
    renew: "Продлить",
    renewAria: "Продлить: поиск и вопросы специалисту",
    remind7: (n, day) =>
        `${ruPlural(n, ["Остался", "Осталось", "Осталось"])} ${n} ${ruPlural(n, ["вопрос", "вопроса", "вопросов"])} до ${day} — продлите, и ${n === 1 ? "он сохранится" : "они сохранятся"}.`,
    remind1: (n) =>
        `${ruPlural(n, ["Остался", "Осталось", "Осталось"])} ${n} ${ruPlural(n, ["вопрос", "вопроса", "вопросов"])} до завтра — продлите, и ${n === 1 ? "он сохранится" : "они сохранятся"}.`,
    chipAria: (d) => `Поиск открыт до ${d}. Продлить`,
    searchEnded: (d) => `Поиск был открыт до ${d}`,
    newAfterRenew: "Новый вопрос — после продления",
    migration: (tokens, questions, d) => [
        { t: ruPlural(tokens, ["Ваш ", "Ваши ", "Ваши "]) },
        { t: `${tokens} ${ruPlural(tokens, ["токен", "токена", "токенов"])}`, b: true },
        { t: ruPlural(tokens, [" стал ", " стали ", " стали "]) },
        ...(questions > 0
            ? [
                  {
                      t: `${questions} ${ruPlural(questions, ["вопросом", "вопросами", "вопросами"])} специалисту`,
                      b: true,
                  },
                  { t: " и поиском до " },
              ]
            : [{ t: "поиском до " }]),
        { t: d, b: true },
    ],
    gotIt: "Понятно",
    trialTitle: "7 дней поиска по клипам — в подарок",
    trialSub: (d) => `до ${d}`,
    appYearTitle: "+1 месяц поиска по клипам за год подписки приложения",
    appYearSub: (d) => `поиск открыт до ${d}`,
    buyerMonthTitle: "Месяц поиска по клипам и лекциям — в подарок за покупку",
    buyerMonthSub: (d) => `поиск открыт до ${d}`,
    dailyCap: "Продолжим завтра",
    paidToast: (term, d) => (term ? `Пакет на ${term} — до ${d}` : `Поиск открыт до ${d}`),
    insistNote: "Такой вопрос смотрит менеджер — вопрос из пакета он не расходует.",
    loadFailed: "Не удалось загрузить.",
    retry: "Повторить",
    guestTrial: "7 дней поиска — в подарок",
    guestLogin: "войти",
    guestTrialNote: "После входа — 7 дней поиска по клипам в подарок",
    redeemTitle: "Получить по ссылке",
    redeemDone: "Готово — вопросы специалисту и поиск добавлены.",
    productGives: "Умный поиск по лекциям и вопросы специалисту — на срок пакета",
};

const enQ = (n: number) => (n === 1 ? "question" : "questions");

const EN: PlanCopy = {
    packsTitle: "Search and specialist questions",
    packsSub: "Smart search for the whole term and questions to a specialist",
    subLine: "Discount for the annual app subscription",
    term: (m) => `${m} ${m === 1 ? "month" : "months"}`,
    givesSearch: (term) => `Smart search — ${term}`,
    questionsTail: (n) => `${enQ(n)} to a specialist`,
    choose: "Choose",
    chooseAria: (term, price, regular) =>
        regular ? `Choose ${term}: ${price} instead of ${regular}` : `Choose ${term}: ${price}`,
    foot: "Renew before the end — the questions you have left are kept.",
    quiet: "Cheaper with the annual app subscription",
    close: "Close",
    checkoutFailed: "Could not open the payment. Please try again.",
    chatLabel: "Specialist questions:",
    until: (d) => `until ${d}`,
    ended: (d) => `ended ${d}`,
    renew: "Renew",
    renewAria: "Renew: search and specialist questions",
    remind7: (n, day) => `${n} ${enQ(n)} left until ${day} — renew and ${n === 1 ? "it is" : "they are"} kept.`,
    remind1: (n) => `${n} ${enQ(n)} left until tomorrow — renew and ${n === 1 ? "it is" : "they are"} kept.`,
    chipAria: (d) => `Search open until ${d}. Renew`,
    searchEnded: (d) => `Search was open until ${d}`,
    newAfterRenew: "New question — after renewal",
    migration: (tokens, questions, d) => [
        { t: "Your " },
        { t: `${tokens} ${tokens === 1 ? "token" : "tokens"}`, b: true },
        { t: " became " },
        ...(questions > 0
            ? [
                  { t: `${questions} specialist ${enQ(questions)}`, b: true },
                  { t: " and search until " },
              ]
            : [{ t: "search until " }]),
        { t: d, b: true },
    ],
    gotIt: "Got it",
    trialTitle: "7 days of clip search — a gift",
    trialSub: (d) => `until ${d}`,
    appYearTitle: "+1 month of clip search for a year of the app subscription",
    appYearSub: (d) => `search open until ${d}`,
    buyerMonthTitle: "A month of clip and lecture search — a gift for your purchase",
    buyerMonthSub: (d) => `search open until ${d}`,
    dailyCap: "Let's continue tomorrow",
    paidToast: (term, d) => (term ? `${term} pack — until ${d}` : `Search open until ${d}`),
    insistNote: "A manager looks at such a question — it does not use a question from your pack.",
    loadFailed: "Could not load.",
    retry: "Retry",
    guestTrial: "7 days of search — a gift",
    guestLogin: "sign in",
    guestTrialNote: "After signing in — 7 days of clip search as a gift",
    redeemTitle: "Redeem a link",
    redeemDone: "Done — specialist questions and search added.",
    productGives: "Smart search across the lectures and specialist questions — for the pack's term",
};

const deQ = (n: number) => (n === 1 ? "Frage" : "Fragen");

const DE: PlanCopy = {
    packsTitle: "Suche und Fragen an Spezialisten",
    packsSub: "Intelligente Suche für die ganze Laufzeit und Fragen an einen Spezialisten",
    subLine: "Rabatt für das App-Jahresabo",
    term: (m) => `${m} ${m === 1 ? "Monat" : "Monate"}`,
    givesSearch: (term) => `Intelligente Suche — ${term}`,
    questionsTail: (n) => `${deQ(n)} an Spezialisten`,
    choose: "Wählen",
    chooseAria: (term, price, regular) =>
        regular ? `${term} wählen: ${price} statt ${regular}` : `${term} wählen: ${price}`,
    foot: "Vor Ablauf verlängern — übrige Fragen bleiben erhalten.",
    quiet: "Mit dem App-Jahresabo günstiger",
    close: "Schließen",
    checkoutFailed: "Die Zahlung ließ sich nicht öffnen. Bitte erneut versuchen.",
    chatLabel: "Fragen an Spezialisten:",
    until: (d) => `bis ${d}`,
    ended: (d) => `abgelaufen am ${d}`,
    renew: "Verlängern",
    renewAria: "Verlängern: Suche und Fragen an Spezialisten",
    remind7: (n, day) => `Noch ${n} ${deQ(n)} bis ${day} — verlängern Sie, dann ${n === 1 ? "bleibt sie" : "bleiben sie"} erhalten.`,
    remind1: (n) => `Noch ${n} ${deQ(n)} bis morgen — verlängern Sie, dann ${n === 1 ? "bleibt sie" : "bleiben sie"} erhalten.`,
    chipAria: (d) => `Suche offen bis ${d}. Verlängern`,
    searchEnded: (d) => `Die Suche war bis ${d} offen`,
    newAfterRenew: "Neue Frage — nach der Verlängerung",
    migration: (tokens, questions, d) => [
        { t: "Aus Ihren " },
        { t: `${tokens} Token`, b: true },
        { t: " wurden " },
        ...(questions > 0
            ? [
                  { t: `${questions} ${deQ(questions)} an Spezialisten`, b: true },
                  { t: " und die Suche bis " },
              ]
            : [{ t: "die Suche bis " }]),
        { t: d, b: true },
    ],
    gotIt: "Verstanden",
    trialTitle: "7 Tage Clip-Suche — als Geschenk",
    trialSub: (d) => `bis ${d}`,
    appYearTitle: "+1 Monat Clip-Suche für ein Jahr App-Abo",
    appYearSub: (d) => `Suche offen bis ${d}`,
    buyerMonthTitle: "Ein Monat Suche in Clips und Vorträgen — als Geschenk für Ihren Kauf",
    buyerMonthSub: (d) => `Suche offen bis ${d}`,
    dailyCap: "Morgen geht es weiter",
    paidToast: (term, d) => (term ? `Paket für ${term} — bis ${d}` : `Suche offen bis ${d}`),
    insistNote: "Eine solche Frage sieht sich ein Manager an — sie verbraucht keine Frage aus Ihrem Paket.",
    loadFailed: "Laden fehlgeschlagen.",
    retry: "Erneut versuchen",
    guestTrial: "7 Tage Suche — als Geschenk",
    guestLogin: "anmelden",
    guestTrialNote: "Nach der Anmeldung — 7 Tage Clip-Suche als Geschenk",
    redeemTitle: "Link einlösen",
    redeemDone: "Fertig — Fragen an Spezialisten und Suche hinzugefügt.",
    productGives: "Intelligente Suche in den Vorträgen und Fragen an Spezialisten — für die Laufzeit des Pakets",
};

const esQ = (n: number) => (n === 1 ? "pregunta" : "preguntas");

const ES: PlanCopy = {
    packsTitle: "Búsqueda y preguntas al especialista",
    packsSub: "Búsqueda inteligente durante todo el plazo y preguntas a un especialista",
    subLine: "Descuento por la suscripción anual de la app",
    term: (m) => `${m} ${m === 1 ? "mes" : "meses"}`,
    givesSearch: (term) => `Búsqueda inteligente — ${term}`,
    questionsTail: (n) => `${esQ(n)} al especialista`,
    choose: "Elegir",
    chooseAria: (term, price, regular) =>
        regular ? `Elegir ${term}: ${price} en lugar de ${regular}` : `Elegir ${term}: ${price}`,
    foot: "Renueve antes del final y conservará las preguntas que le queden.",
    quiet: "Más barato con la suscripción anual de la app",
    close: "Cerrar",
    checkoutFailed: "No se pudo abrir el pago. Inténtelo de nuevo.",
    chatLabel: "Preguntas al especialista:",
    until: (d) => `hasta el ${d}`,
    ended: (d) => `terminó el ${d}`,
    renew: "Renovar",
    renewAria: "Renovar: búsqueda y preguntas al especialista",
    remind7: (n, day) => `${n === 1 ? "Le queda" : "Le quedan"} ${n} ${esQ(n)} hasta el ${day}: renueve y se ${n === 1 ? "conservará" : "conservarán"}.`,
    remind1: (n) => `${n === 1 ? "Le queda" : "Le quedan"} ${n} ${esQ(n)} hasta mañana: renueve y se ${n === 1 ? "conservará" : "conservarán"}.`,
    chipAria: (d) => `Búsqueda abierta hasta el ${d}. Renovar`,
    searchEnded: (d) => `La búsqueda estuvo abierta hasta el ${d}`,
    newAfterRenew: "Nueva pregunta: después de renovar",
    migration: (tokens, questions, d) => [
        { t: "Sus " },
        { t: `${tokens} ${tokens === 1 ? "token" : "tokens"}`, b: true },
        { t: " se convirtieron en " },
        ...(questions > 0
            ? [
                  { t: `${questions} ${esQ(questions)} al especialista`, b: true },
                  { t: " y búsqueda hasta el " },
              ]
            : [{ t: "búsqueda hasta el " }]),
        { t: d, b: true },
    ],
    gotIt: "Entendido",
    trialTitle: "7 días de búsqueda de clips: de regalo",
    trialSub: (d) => `hasta el ${d}`,
    appYearTitle: "+1 mes de búsqueda de clips por un año de suscripción a la app",
    appYearSub: (d) => `búsqueda abierta hasta el ${d}`,
    buyerMonthTitle: "Un mes de búsqueda en clips y conferencias: de regalo por su compra",
    buyerMonthSub: (d) => `búsqueda abierta hasta el ${d}`,
    dailyCap: "Seguimos mañana",
    paidToast: (term, d) => (term ? `Paquete de ${term}: hasta el ${d}` : `Búsqueda abierta hasta el ${d}`),
    insistNote: "Una pregunta así la revisa un gestor: no gasta ninguna pregunta de su paquete.",
    loadFailed: "No se pudo cargar.",
    retry: "Reintentar",
    guestTrial: "7 días de búsqueda: de regalo",
    guestLogin: "entrar",
    guestTrialNote: "Al entrar: 7 días de búsqueda de clips de regalo",
    redeemTitle: "Canjear un enlace",
    redeemDone: "Listo: se añadieron preguntas al especialista y búsqueda.",
    productGives: "Búsqueda inteligente en las conferencias y preguntas al especialista durante el plazo del paquete",
};

const CN: PlanCopy = {
    packsTitle: "搜索和专家提问",
    packsSub: "整个期限内的智能搜索和专家提问",
    subLine: "应用年度订阅折扣",
    term: (m) => `${m} 个月`,
    givesSearch: (term) => `智能搜索 — ${term}`,
    questionsTail: () => "个专家提问",
    choose: "选择",
    chooseAria: (term, price, regular) => (regular ? `选择 ${term}:${price}(原价 ${regular})` : `选择 ${term}:${price}`),
    foot: "在到期前续订 — 剩余的提问会保留。",
    quiet: "有应用年度订阅更便宜",
    close: "关闭",
    checkoutFailed: "无法打开付款页面,请重试。",
    chatLabel: "专家提问:",
    until: (d) => `至 ${d}`,
    ended: (d) => `已于 ${d} 到期`,
    renew: "续订",
    renewAria: "续订:搜索和专家提问",
    remind7: (n, day) => `还剩 ${n} 个提问,至 ${day} 到期 — 续订即可保留。`,
    remind1: (n) => `还剩 ${n} 个提问,明天到期 — 续订即可保留。`,
    chipAria: (d) => `搜索开放至 ${d}。续订`,
    searchEnded: (d) => `搜索已于 ${d} 结束`,
    newAfterRenew: "新问题 — 续订后可提",
    migration: (tokens, questions, d) => [
        { t: "您的 " },
        { t: `${tokens} 个代币`, b: true },
        { t: " 已转换为 " },
        ...(questions > 0
            ? [
                  { t: `${questions} 个专家提问`, b: true },
                  { t: " 和搜索,至 " },
              ]
            : [{ t: "搜索,至 " }]),
        { t: d, b: true },
    ],
    gotIt: "知道了",
    trialTitle: "7 天片段搜索 — 赠送",
    trialSub: (d) => `至 ${d}`,
    appYearTitle: "应用订阅每满一年 +1 个月片段搜索",
    appYearSub: (d) => `搜索开放至 ${d}`,
    buyerMonthTitle: "一个月片段和讲座搜索 — 购买赠礼",
    buyerMonthSub: (d) => `搜索开放至 ${d}`,
    dailyCap: "明天继续",
    paidToast: (term, d) => (term ? `${term}套餐 — 至 ${d}` : `搜索开放至 ${d}`),
    insistNote: "此类问题由管理员查看 — 不消耗套餐中的提问。",
    loadFailed: "加载失败。",
    retry: "重试",
    guestTrial: "7 天搜索 — 赠送",
    guestLogin: "登录",
    guestTrialNote: "登录后赠送 7 天片段搜索",
    redeemTitle: "通过链接领取",
    redeemDone: "完成 — 已添加专家提问和搜索。",
    productGives: "在套餐期限内的讲座智能搜索和专家提问",
};

/** Арабские формы при числе: 1–2 — «سؤال», 3–10 — «أسئلة», 11+ — «سؤالًا». */
const arQ = (n: number) => {
    const a = Math.abs(n) % 100;
    if (a >= 3 && a <= 10) return "أسئلة";
    if (a >= 11) return "سؤالًا";
    return "سؤال";
};

const arMonths = (m: number) => (m === 1 ? "شهر واحد" : m === 2 ? "شهران" : m <= 10 ? `${m} أشهر` : `${m} شهرًا`);

const AR: PlanCopy = {
    packsTitle: "البحث وأسئلة المختص",
    packsSub: "بحث ذكي طوال المدة وأسئلة إلى مختص",
    subLine: "خصم لاشتراك التطبيق السنوي",
    term: (m) => arMonths(m),
    givesSearch: (term) => `البحث الذكي — ${term}`,
    questionsTail: (n) => `${arQ(n)} إلى المختص`,
    choose: "اختيار",
    chooseAria: (term, price, regular) =>
        regular ? `اختيار ${term}: ${price} بدلًا من ${regular}` : `اختيار ${term}: ${price}`,
    foot: "جدّد قبل انتهاء المدة — تبقى الأسئلة المتبقية.",
    quiet: "أرخص مع اشتراك التطبيق السنوي",
    close: "إغلاق",
    checkoutFailed: "تعذّر فتح الدفع. حاول مرة أخرى.",
    chatLabel: "أسئلة إلى المختص:",
    until: (d) => `حتى ${d}`,
    ended: (d) => `انتهت المدة في ${d}`,
    renew: "تجديد",
    renewAria: "تجديد: البحث وأسئلة المختص",
    remind7: (n, day) => `تبقّى ${n} ${arQ(n)} حتى ${day} — جدّد لتحتفظ بها.`,
    remind1: (n) => `تبقّى ${n} ${arQ(n)} حتى الغد — جدّد لتحتفظ بها.`,
    chipAria: (d) => `البحث مفتوح حتى ${d}. تجديد`,
    searchEnded: (d) => `كان البحث مفتوحًا حتى ${d}`,
    newAfterRenew: "سؤال جديد — بعد التجديد",
    migration: (tokens, questions, d) => [
        { t: "تحوّلت " },
        { t: `${tokens} رمزًا`, b: true },
        { t: " إلى " },
        ...(questions > 0
            ? [
                  { t: `${questions} ${arQ(questions)} إلى المختص`, b: true },
                  { t: " وبحث حتى " },
              ]
            : [{ t: "بحث حتى " }]),
        { t: d, b: true },
    ],
    gotIt: "حسنًا",
    trialTitle: "7 أيام من البحث في المقاطع — هدية",
    trialSub: (d) => `حتى ${d}`,
    appYearTitle: "+شهر من البحث في المقاطع مقابل سنة من اشتراك التطبيق",
    appYearSub: (d) => `البحث مفتوح حتى ${d}`,
    buyerMonthTitle: "شهر من البحث في المقاطع والمحاضرات — هدية مقابل الشراء",
    buyerMonthSub: (d) => `البحث مفتوح حتى ${d}`,
    dailyCap: "نكمل غدًا",
    paidToast: (term, d) => (term ? `باقة ${term} — حتى ${d}` : `البحث مفتوح حتى ${d}`),
    insistNote: "يطّلع مدير على مثل هذا السؤال — ولا يُحتسب من أسئلة باقتك.",
    loadFailed: "تعذّر التحميل.",
    retry: "إعادة المحاولة",
    guestTrial: "7 أيام من البحث — هدية",
    guestLogin: "تسجيل الدخول",
    guestTrialNote: "بعد تسجيل الدخول — 7 أيام من البحث في المقاطع هدية",
    redeemTitle: "الاستلام عبر رابط",
    redeemDone: "تم — أُضيفت أسئلة المختص والبحث.",
    productGives: "بحث ذكي في المحاضرات وأسئلة إلى المختص — طوال مدة الباقة",
};

const COPY: Record<PlanLang, PlanCopy> = { ru: RU, en: EN, de: DE, es: ES, cn: CN, ar: AR };

/**
 * «Клипы и лекции» (владелец 11.10: лекции — часть умного поиска) — только тем, кому лекции уже
 * под воротами (planStore.lecturesGateOn; владелец 12.10: пока выключатель не «all» — админам и
 * тестерам). Остальным — прежние строки выше, как до 11.10.
 */
type LectureTexts = Pick<PlanCopy, "packsSub" | "givesSearch" | "trialTitle" | "appYearTitle" | "guestTrialNote">;

const WITH_LECTURES: Record<PlanLang, LectureTexts> = {
    ru: {
        packsSub: "Умный поиск по клипам и лекциям на весь срок и вопросы специалисту",
        givesSearch: (term) => `Умный поиск, клипы и лекции — ${term}`,
        trialTitle: "7 дней поиска по клипам и лекциям — в подарок",
        appYearTitle: "+1 месяц поиска по клипам и лекциям за год подписки приложения",
        guestTrialNote: "После входа — 7 дней поиска по клипам и лекциям в подарок",
    },
    en: {
        packsSub: "Smart search across clips and lectures for the whole term and questions to a specialist",
        givesSearch: (term) => `Smart search, clips and lectures — ${term}`,
        trialTitle: "7 days of clip and lecture search — a gift",
        appYearTitle: "+1 month of clip and lecture search for a year of the app subscription",
        guestTrialNote: "After signing in — 7 days of clip and lecture search as a gift",
    },
    de: {
        packsSub: "Intelligente Suche in Clips und Vorträgen für die ganze Laufzeit und Fragen an einen Spezialisten",
        givesSearch: (term) => `Intelligente Suche, Clips und Vorträge — ${term}`,
        trialTitle: "7 Tage Suche in Clips und Vorträgen — als Geschenk",
        appYearTitle: "+1 Monat Suche in Clips und Vorträgen für ein Jahr App-Abo",
        guestTrialNote: "Nach der Anmeldung — 7 Tage Suche in Clips und Vorträgen als Geschenk",
    },
    es: {
        packsSub: "Búsqueda inteligente en clips y conferencias durante todo el plazo y preguntas a un especialista",
        givesSearch: (term) => `Búsqueda inteligente, clips y conferencias — ${term}`,
        trialTitle: "7 días de búsqueda en clips y conferencias: de regalo",
        appYearTitle: "+1 mes de búsqueda en clips y conferencias por un año de suscripción a la app",
        guestTrialNote: "Al entrar: 7 días de búsqueda en clips y conferencias de regalo",
    },
    cn: {
        packsSub: "整个期限内的片段和讲座智能搜索及专家提问",
        givesSearch: (term) => `智能搜索、片段和讲座 — ${term}`,
        trialTitle: "7 天片段和讲座搜索 — 赠送",
        appYearTitle: "应用订阅每满一年 +1 个月片段和讲座搜索",
        guestTrialNote: "登录后赠送 7 天片段和讲座搜索",
    },
    ar: {
        packsSub: "بحث ذكي في المقاطع والمحاضرات طوال المدة وأسئلة إلى مختص",
        givesSearch: (term) => `البحث الذكي والمقاطع والمحاضرات — ${term}`,
        trialTitle: "7 أيام من البحث في المقاطع والمحاضرات — هدية",
        appYearTitle: "+شهر من البحث في المقاطع والمحاضرات مقابل سنة من اشتراك التطبيق",
        guestTrialNote: "بعد تسجيل الدخول — 7 أيام من البحث في المقاطع والمحاضرات هدية",
    },
};

const COPY_LECTURES = Object.fromEntries(
    LANGS.map((l) => [l, { ...COPY[l], ...WITH_LECTURES[l] }]),
) as Record<PlanLang, PlanCopy>;

/** Тексты пакетов; `lectures` — лекции у человека под воротами (по умолчанию — как известно сейчас). */
export const planCopy = (lang: string | undefined | null, lectures: boolean = lecturesGateOn()): PlanCopy =>
    (lectures ? COPY_LECTURES : COPY)[planLang(lang)];

/**
 * Строки СУП v2, где в режиме пакетов не должно быть «токенов» (поиск открыт
 * на срок, считает сервер). Ключ — ключ supV2.* общего словаря; нет здесь —
 * берётся обычная строка. chargeNowFirstClip / chargeOnOpenNote — строка админа
 * в подборке: когда поиск засчитывается (сервер ведёт ему счёт за сутки), а не
 * когда списывается токен.
 */
const SUP_V2_PLAN: Record<PlanLang, Record<string, string>> = {
    ru: {
        "supV2.submitSearch": "Искать по лекциям",
        "supV2.walletLoading": "Загружаем…",
        "supV2.walletFailed": "Не удалось загрузить. Попробуйте ещё раз.",
        "supV2.listEmpty": "По этому вопросу клипов не нашлось.",
        "supV2.listError": "Подборка сейчас не собирается. Попробуйте ещё раз.",
        "supV2.chargeNowFirstClip": "Сейчас поиск засчитывается при первом клипе.",
        "supV2.chargeOnOpenNote": "Засчитывать при открытии подборки начнём после согласования.",
    },
    en: {
        "supV2.submitSearch": "Search the lectures",
        "supV2.walletLoading": "Loading…",
        "supV2.walletFailed": "Could not load. Please try again.",
        "supV2.listEmpty": "No clips found for this question.",
        "supV2.listError": "The clips could not be collected right now. Please try again.",
        "supV2.chargeNowFirstClip": "Now the search counts at the first clip.",
        "supV2.chargeOnOpenNote": "Counting it on opening the list turns on after approval.",
    },
    de: {
        "supV2.submitSearch": "In den Vorträgen suchen",
        "supV2.walletLoading": "Wird geladen…",
        "supV2.walletFailed": "Laden fehlgeschlagen. Bitte erneut versuchen.",
        "supV2.listEmpty": "Zu dieser Frage wurden keine Clips gefunden.",
        "supV2.listError": "Die Auswahl lässt sich gerade nicht zusammenstellen. Bitte erneut versuchen.",
        "supV2.chargeNowFirstClip": "Jetzt zählt die Suche beim ersten Clip.",
        "supV2.chargeOnOpenNote": "Das Zählen beim Öffnen der Auswahl startet nach Freigabe.",
    },
    es: {
        "supV2.submitSearch": "Buscar en las conferencias",
        "supV2.walletLoading": "Cargando…",
        "supV2.walletFailed": "No se pudo cargar. Inténtelo de nuevo.",
        "supV2.listEmpty": "No se encontraron clips para esta pregunta.",
        "supV2.listError": "Ahora no se puede reunir la selección. Inténtelo de nuevo.",
        "supV2.chargeNowFirstClip": "Ahora la búsqueda cuenta con el primer clip.",
        "supV2.chargeOnOpenNote": "Contarla al abrir la selección se activará tras la aprobación.",
    },
    cn: {
        "supV2.submitSearch": "在讲座中搜索",
        "supV2.walletLoading": "加载中…",
        "supV2.walletFailed": "加载失败,请重试。",
        "supV2.listEmpty": "没有找到与此问题相关的片段。",
        "supV2.listError": "暂时无法生成片段合集,请重试。",
        "supV2.chargeNowFirstClip": "目前在播放第一个片段时计入搜索。",
        "supV2.chargeOnOpenNote": "打开合集时计入将在批准后启用。",
    },
    ar: {
        "supV2.submitSearch": "البحث في المحاضرات",
        "supV2.walletLoading": "جارٍ التحميل…",
        "supV2.walletFailed": "تعذّر التحميل. حاول مرة أخرى.",
        "supV2.listEmpty": "لم يُعثر على مقاطع لهذا السؤال.",
        "supV2.listError": "تعذّر جمع المقاطع الآن. حاول مرة أخرى.",
        "supV2.chargeNowFirstClip": "الآن يُحتسب البحث عند أول مقطع.",
        "supV2.chargeOnOpenNote": "الاحتساب عند فتح المجموعة يُفعَّل بعد الموافقة.",
    },
};

export const supV2PlanText = (lang: string | undefined | null, key: string): string | undefined =>
    SUP_V2_PLAN[planLang(lang)][key];
